import { useEffect, useState } from 'react';
import { CheckCircleIcon, XCircleIcon, ClockIcon } from '@heroicons/react/24/outline';
import { geneticsApi } from '../../api/genetics';

interface BreedingPairOutcomesPanelProps {
  breedingPairId: string;
}

export default function BreedingPairOutcomesPanel({ breedingPairId }: BreedingPairOutcomesPanelProps) {
  const [report, setReport] = useState<Awaited<ReturnType<typeof geneticsApi.getBreedingOutcomesReport>> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!breedingPairId) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await geneticsApi.getBreedingOutcomesReport({ breedingPairId, limit: 50 });
        if (!cancelled) setReport(result);
      } catch (err: unknown) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load outcomes');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [breedingPairId]);

  if (loading) return <p className="text-sm text-slate-500 mt-4">Loading hatch outcome history…</p>;
  if (error) return <p className="text-sm text-red-600 mt-4">{error}</p>;
  if (!report?.totalHatchSnapshots) return null;

  return (
    <div className="mt-4 rounded-lg border border-slate-200 bg-white p-4">
      <h4 className="text-sm font-semibold text-slate-900 mb-2">Hatch Outcome History</h4>
      <p className="text-xs text-slate-600 mb-3">
        {report.totalHatchSnapshots} hatch snapshot{report.totalHatchSnapshots === 1 ? '' : 's'}
        {report.bandedWithOutcome > 0 && (
          <> · {(report.matchRate * 100).toFixed(0)}% matched prediction after banding</>
        )}
      </p>

      <div className="space-y-2 max-h-48 overflow-y-auto">
        {report.records.map((record) => {
          const hasOutcome = record.matched !== null && record.matched !== undefined;
          return (
            <div key={record.id} className="text-xs rounded border border-slate-100 bg-slate-50 px-2 py-1.5">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  {record.topPrediction && (
                    <p className="text-slate-800 truncate">
                      Expected: {record.topPrediction}
                      {record.topPredictionProbability != null && (
                        <span className="text-slate-500"> ({(record.topPredictionProbability * 100).toFixed(0)}%)</span>
                      )}
                    </p>
                  )}
                  {hasOutcome ? (
                    <p className="text-slate-700 truncate">
                      Actual: {record.actualPhenotype || record.actualMutationName || '—'}
                    </p>
                  ) : (
                    <p className="text-slate-500 flex items-center gap-1">
                      <ClockIcon className="h-3.5 w-3.5" />
                      Awaiting banding
                    </p>
                  )}
                </div>
                {hasOutcome &&
                  (record.matched ? (
                    <CheckCircleIcon className="h-4 w-4 text-green-600 flex-shrink-0" />
                  ) : (
                    <XCircleIcon className="h-4 w-4 text-amber-600 flex-shrink-0" />
                  ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
